import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    QrCode,
    ClipboardList,
    Play,
    CheckCircle2,
    Wrench,
    XCircle,
    ShieldCheck,
    Camera,
    MessageSquarePlus,
    History,
    ExternalLink,
} from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { assetsService } from "@/services/assets.service";
import { formatDateTime, cn } from "@/lib/utils";
import { WO_STATUS_LABELS } from "@/config/constants";

// MVP 0.1 event catalog
function getEventVisual(event) {
    const t = event.type;
    const action = event?.meta?.action;

    if (t === "asset_created") {
        return { Icon: QrCode, tone: "secondary" };
    }
    if (t === "work_order_created") {
        return { Icon: ClipboardList, tone: "secondary" };
    }
    if (t === "work_order_event") {
        switch (action) {
            case "accepted":
                return { Icon: ShieldCheck, tone: "secondary" };
            case "started":
                return { Icon: Play, tone: "warning" };
            case "note_added":
                return { Icon: MessageSquarePlus, tone: "muted" };
            case "photo_added":
                return { Icon: Camera, tone: "muted" };
            case "finished":
                return { Icon: CheckCircle2, tone: "success" };
            case "cancelled":
                return { Icon: XCircle, tone: "danger" };
            default:
                return { Icon: Wrench, tone: "muted" };
        }
    }
    return { Icon: History, tone: "muted" };
}

const TONE_CLASSES = {
    secondary: {
        wrap: "bg-secondary/10 text-secondary ring-secondary/20",
        badge: "border-secondary/30 text-secondary bg-secondary/5",
    },
    success: {
        wrap: "bg-success/10 text-success ring-success/20",
        badge: "border-success/30 text-success bg-success/5",
    },
    warning: {
        wrap: "bg-warning/10 text-warning ring-warning/20",
        badge: "border-warning/30 text-warning bg-warning/5",
    },
    danger: {
        wrap: "bg-danger/10 text-danger ring-danger/20",
        badge: "border-danger/30 text-danger bg-danger/5",
    },
    muted: {
        wrap: "bg-muted text-muted-foreground ring-border",
        badge: "border-border text-muted-foreground bg-muted",
    },
};

function getMetaBadge(event) {
    if (event.type === "work_order_created" && event?.meta?.status) {
        return WO_STATUS_LABELS[event.meta.status] || event.meta.status;
    }
    return null;
}

function TimelineRow({ event, isLast }) {
    const { Icon, tone } = getEventVisual(event);
    const tc = TONE_CLASSES[tone] || TONE_CLASSES.muted;
    const navigate = useNavigate();

    const canNavigate = event.ref_type === "work_order";
    const targetPath = canNavigate ? `/work-orders/${event.ref_id}` : null;
    const statusBadge = getMetaBadge(event);

    return (
        <li
            className="relative flex gap-3 pb-6"
            data-testid={`asset-timeline-item-${event.ref_type}`}
        >
            {!isLast && (
                <span
                    aria-hidden="true"
                    className="absolute left-4 top-8 h-full w-px bg-border"
                />
            )}

            <div
                className={cn(
                    "relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1",
                    tc.wrap
                )}
            >
                <Icon className="h-4 w-4" strokeWidth={2} />
            </div>

            <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <p className="text-sm font-medium text-foreground">{event.title}</p>
                    <span className="text-[11px] text-muted-foreground">
                        {formatDateTime(event.at)}
                    </span>
                </div>

                {event.description && (
                    <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                        {event.description}
                    </p>
                )}

                {(statusBadge || canNavigate) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        {statusBadge && (
                            <Badge
                                variant="outline"
                                className={cn("h-5 rounded-full px-2 text-[10px] font-medium", tc.badge)}
                                data-testid="asset-timeline-status-badge"
                            >
                                {statusBadge}
                            </Badge>
                        )}
                        {canNavigate && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[11px] text-secondary hover:bg-secondary/5 hover:text-secondary"
                                onClick={() => navigate(targetPath)}
                                data-testid={`asset-timeline-open-${event.ref_type}-${event.ref_id}`}
                            >
                                {event.ref_code || "Abrir"}
                                <ExternalLink className="ml-1 h-3 w-3" />
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </li>
    );
}

export function AssetTimeline({ assetId }) {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ["asset-timeline", assetId],
        queryFn: () => assetsService.getTimeline(assetId),
        enabled: !!assetId,
    });

    if (isLoading) {
        return (
            <div className="space-y-4" data-testid="asset-timeline-loading">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="flex gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-3.5 w-2/3" />
                            <Skeleton className="h-3 w-1/3" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (isError) {
        return (
            <div
                className="flex flex-col items-start gap-2 rounded-lg border border-dashed bg-muted/40 px-4 py-6 text-sm text-muted-foreground"
                data-testid="asset-timeline-error"
            >
                <p>Não foi possível carregar o histórico do equipamento.</p>
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                    Tentar novamente
                </Button>
            </div>
        );
    }

    const events = Array.isArray(data) ? data : [];

    if (events.length === 0) {
        return (
            <div
                className="rounded-lg border border-dashed bg-muted/40 px-4 py-8 text-center text-xs text-muted-foreground"
                data-testid="asset-timeline-empty"
            >
                Ainda não há eventos registrados para este equipamento.
            </div>
        );
    }

    return (
        <ol className="relative" data-testid="asset-timeline">
            {events.map((event, i) => (
                <TimelineRow
                    key={`${event.ref_type}-${event.ref_id}-${event.type}-${i}`}
                    event={event}
                    isLast={i === events.length - 1}
                />
            ))}
        </ol>
    );
}

export default AssetTimeline;
